/**
 * Waterloo Game Definition
 *
 * boardgame.io game object that defines the game rules.
 * Implements Bavarian card game with General, Defenders, and Stash positions.
 */

import { Stage, INVALID_MOVE } from 'boardgame.io/dist/esm/core.js';
import { getCardValue, attackExceedsTarget, isAce } from './cardUtils.js';

/**
 * Select the General (highest value card) from drawn cards.
 * Returns { general, defenders } where defenders are the remaining cards.
 */
function selectGeneral(drawnCards) {
  if (drawnCards.length < 3) {
    throw new Error('Need exactly 3 cards for general selection');
  }

  let highestIdx = 0;
  let highestValue = getCardValue(drawnCards[0]);

  for (let i = 1; i < drawnCards.length; i++) {
    const value = getCardValue(drawnCards[i]);
    if (value > highestValue) {
      highestValue = value;
      highestIdx = i;
    }
  }

  const general =drawnCards[highestIdx];
  const defenders = drawnCards.filter((_, i) => i !== highestIdx);

  return { general, defenders };
}

/**
 * Collect all cards from a defeated player.
 * Includes: General + attackers, Defenders + attackers, Stack, Hand, Stash
 * @param {object} player - The defeated player state
 * @returns {string[]} All card IDs from the player
 */
function collectDefeatedPlayerCards(player) {
  const cards = [];

  // General + its attackers
  if (player.general) {
    if (player.general.card) cards.push(player.general.card);
    cards.push(...(player.general.attacks || []));
  }

  // Defenders + their attackers
  if (player.defender1) {
    if (player.defender1.card) cards.push(player.defender1.card);
    cards.push(...(player.defender1.attacks || []));
  }
  if (player.defender2) {
    if (player.defender2.card) cards.push(player.defender2.card);
    cards.push(...(player.defender2.attacks || []));
  }

  // Stack, Hand, Stash
  cards.push(...(player.stack || []));
  cards.push(...(player.hand || []));
  if (player.stash) cards.push(player.stash);

  return cards;
}

/**
 * Shuffle an array in-place using Fisher-Yates.
 * @param {Array} array - Array to shuffle
 * @returns {Array} The same array, shuffled
 */
function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

/**
 * Clear all card positions for a defeated player.
 * @param {object} player - The defeated player state
 */
function clearDefeatedPlayerCards(player) {
  player.general = null;
  player.defender1 = null;
  player.defender2 = null;
  player.stack = [];
  player.hand = [];
  player.stash = null;
}

/**
 * Detect if there's a winner (only one player with a general remaining).
 * @param {object} G - Game state
 * @returns {string|null} Winner player ID or null if game continues
 */
function detectWinner(G) {
  const activePlayers = Object.entries(G.players)
    .filter(([_, p]) => p.general && p.general.card)
    .map(([id]) => id);
  return activePlayers.length === 1 ? activePlayers[0] : null;
}

/**
 * Create a shuffled Bavarian 36-card deck.
 * Cards are represented as shortName strings: 'h6', 's13', 'd1' (Ace), etc.
 * Ranks: 6, 7, 8, 9, 10, 11 (Jack/Unter), 12 (Queen/Ober), 13 (King/König), 1 (Ace/Ass)
 */
function createBavarianDeck() {
  const suits = ['h', 's', 'd', 'c'];
  const ranks = [6, 7, 8, 9, 10, 11, 12, 13, 1]; // 6-K plus Ace

  const deck = [];
  for (const suit of suits) {
    for (const rank of ranks) {
      deck.push(suit + rank);
    }
  }

  // Fisher-Yates shuffle
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }

  return deck;
}

const WaterlooGame = {
  name: 'waterloo',

  minPlayers: 2,
  maxPlayers: 4,

  /**
   * Setup initial game state.
   * Starts with all cards in central stack, waiting for Player 0 to initiate.
   * Setup phases: 'waiting' -> 'distributing' -> 'dealing' -> 'play'
   */
  setup: ({ ctx }) => {
    const centralStack = createBavarianDeck();

    const players = {};
    for (let i = 0; i < ctx.numPlayers; i++) {
      players[String(i)] = {
        stack: [],
        hand: [],
        general: null,
        defender1: null,  // First defender card (stackable for attacks)
        defender2: null,  // Second defender card (stackable for attacks)
        stash: null,
      };
    }

    return {
      centralStack,
      players,
      setupPhase: 'waiting',      // waiting, distributing, dealing, play
      currentSetupPlayer: null,   // Which player is currently being dealt
      setupStep: null,            // For dealing: 'draw1', 'draw2', 'draw3', 'general', 'defenders'
      exposedGenerals: [],           // [{playerId, exposedBy}] - generals exposed this turn
      priorityQueue: [],          // [playerId] - players who get priority turns
      currentPriorityPlayer: null, // Player ID receiving priority turn (persists across turn transition)
      turnState: {
        hasDrawn: false,          // Has player drawn cards this turn?
        hasUnstashed: false,      // Has player unstashed this turn?
        hasMadeFirstMove: false,  // Has player made at least one action?
        bonusDrawAllowed: false,  // True after exposing general with last card
        bonusTarget: null,        // Player ID of exposed general (restricts attacks)
        isPriorityTurn: false,    // True if this is a priority turn (3 cards)
      },
      redistributionState: null,  // Active only during 'redistributing' phase
      gameEndingState: null,      // Active only during 'gameEnding' phase { winnerId, step }
      revealState: null,          // Active only during 'revealing' phase for attack animations
      // revealState structure when active:
      // {
      //   attackerId: string,       // Player making the attack
      //   attackCard: string,       // Card being played
      //   targetPlayerId: string,   // Defender owner
      //   targetSlot: number,       // 1 or 2
      //   phase: 'revealing' | 'fanning' | 'collapsing' | 'pausing',
      //   willDefeat: boolean,      // Pre-calculated: will this attack defeat the defender?
      // }
    };
  },

  /**
   * Game moves - actions players can take.
   * Each move receives { G, ctx, playerID } and optional arguments.
   */
  moves: {
    /**
     * Initiate game setup - distributes cards from central stack to player stacks.
     * Only Player 0 can call this when setupPhase is 'waiting'.
     */
    initiateGame: ({ G, ctx, playerID }) => {
      if (G.setupPhase !== 'waiting') return;
      if (playerID !== '0') return; // Only Player 0 can initiate

      const cardsPerPlayer = Math.floor(G.centralStack.length / ctx.numPlayers);

      // Distribute cards to player stacks
      for (let i = 0; i < ctx.numPlayers; i++) {
        const playerCards = G.centralStack.splice(0, cardsPerPlayer);
        G.players[String(i)].stack = playerCards;
      }

      G.setupPhase = 'distributing';
    },

    /**
     * Start the dealing phase after distribution is complete.
     * Triggers the animated dealing sequence.
     */
    startDealing: ({ G }) => {
      if (G.setupPhase !== 'distributing') return;
      G.setupPhase = 'dealing';
      G.currentSetupPlayer = 0;
      G.setupStep = 'draw1';
    },

    /**
     * Execute one step of the dealing animation.
     * Called repeatedly with delays for animated effect.
     */
    dealStep: ({ G, ctx }) => {
      if (G.setupPhase !== 'dealing') return;

      const playerId = String(G.currentSetupPlayer);
      const player = G.players[playerId];
      const step = G.setupStep;

      // Execute current step
      if (step === 'draw1' || step === 'draw2' || step === 'draw3') {
        // Draw one card from stack to hand
        if (player.stack.length > 0) {
          const card = player.stack.shift();
          player.hand.push(card);
        }

        // Move to next draw step or to general
        if (step === 'draw1') G.setupStep = 'draw2';
        else if (step === 'draw2') G.setupStep = 'draw3';
        else G.setupStep = 'general';
      } else if (step === 'general') {
        // Select and place general
        if (player.hand.length >= 3) {
          const { general, defenders } = selectGeneral([...player.hand]);
          player.general = { card: general, attacks: [] };
          player.hand = player.hand.filter(c => c !== general);
        }
        G.setupStep = 'defenders';
      } else if (step === 'defenders') {
        // Place remaining hand cards as defenders (max 2)
        const defenderCards = [...player.hand];
        player.defender1 = defenderCards[0] ? { card: defenderCards[0], attacks: [] } : null;
        player.defender2 = defenderCards[1] ? { card: defenderCards[1], attacks: [] } : null;
        player.hand = [];

        // Move to next player or finish
        const nextPlayer = G.currentSetupPlayer + 1;
        if (nextPlayer < ctx.numPlayers) {
          G.currentSetupPlayer = nextPlayer;
          G.setupStep = 'draw1';
        } else {
          // All players dealt - setup complete
          G.setupPhase = 'play';
          G.currentSetupPlayer = null;
          G.setupStep = null;
        }
      }
    },

    /**
     * Draw cards from stack to hand.
     * Standard start-of-turn action (mutually exclusive with unstash).
     * Draws 3 cards on priority turn, 2 otherwise.
     */
    draw2Cards: ({ G, ctx, playerID }) => {
      if (ctx.currentPlayer !== playerID) return INVALID_MOVE;
      if (G.setupPhase !== 'play') return;
      const player = G.players[ctx.currentPlayer];
      const turn = G.turnState;

      // Validate: not already drawn, not unstashed
      if (turn.hasDrawn) return;
      if (turn.hasUnstashed) return;

      // Determine draw count based on priority turn
      const drawCount = turn.isPriorityTurn ? 3 : 2;
      if (player.stack.length < drawCount) return;

      const drawn = player.stack.splice(0, drawCount);
      player.hand.push(...drawn);
      turn.hasDrawn = true;
      turn.hasMadeFirstMove = true;
    },

    /**
     * Draw card(s) from stack to hand after unstashing.
     * Only allowed after unstashing.
     * Draws 2 cards on priority turn, 1 otherwise.
     */
    draw1Card: ({ G, ctx, playerID }) => {
      if (ctx.currentPlayer !== playerID) return INVALID_MOVE;
      if (G.setupPhase !== 'play') return;
      const player = G.players[ctx.currentPlayer];
      const turn = G.turnState;

      // Validate: not already drawn, must have unstashed
      if (turn.hasDrawn) return;
      if (!turn.hasUnstashed) return;

      // Determine draw count based on priority turn
      // Priority: 3 total (1 unstash + 2 draw)
      // Normal: 2 total (1 unstash + 1 draw)
      const drawCount = turn.isPriorityTurn ? 2 : 1;
      if (player.stack.length < drawCount) return;

      const drawn = player.stack.splice(0, drawCount);
      player.hand.push(...drawn);
      turn.hasDrawn = true;
      turn.hasMadeFirstMove = true;
    },

    /**
     * Create a new defender in an empty slot.
     * @param cardId - Card from hand to place as defender
     * @param slot - 1 or 2, indicating which defender position
     */
    createDefender: ({ G, ctx, playerID }, cardId, slot = 1) => {
      if (ctx.currentPlayer !== playerID) return INVALID_MOVE;
      if (G.setupPhase !== 'play') return;
      const player = G.players[ctx.currentPlayer];
      const cardIndex = player.hand.indexOf(cardId);

      if (cardIndex === -1) return; // Card not in hand

      const defenderKey = slot === 2 ? 'defender2' : 'defender1';
      if (player[defenderKey] !== null) return; // Slot already occupied

      player.hand.splice(cardIndex, 1);
      player[defenderKey] = { card: cardId, attacks: [] };
    },

    /**
     * Attack an opponent's defender with a card from hand.
     * If cumulative attack value exceeds defender, all cards go to attacker's stack.
     * If this exposes the general with the attacker's last card, grants bonus draw.
     * @param cardId - Card from hand to attack with
     * @param targetPlayerId - The player being attacked
     * @param slot - 1 or 2, indicating which defender to attack
     */
    attackDefender: ({ G, ctx, playerID }, cardId, targetPlayerId, slot = 1) => {
      if (ctx.currentPlayer !== playerID) return INVALID_MOVE;
      if (G.setupPhase !== 'play') return;
      const attacker = G.players[ctx.currentPlayer];
      const target = G.players[targetPlayerId];

      if (!target || ctx.currentPlayer === targetPlayerId) return;

      const cardIndex = attacker.hand.indexOf(cardId);
      if (cardIndex === -1) return; // Card not in hand

      const defenderKey = slot === 2 ? 'defender2' : 'defender1';
      const defender = target[defenderKey];
      if (!defender || !defender.card) return; // No defender in that slot

      // Remove card from hand and add to defender's attacks
      attacker.hand.splice(cardIndex, 1);
      defender.attacks.push(cardId);

      // Check if attack exceeds defender value
      if (attackExceedsTarget(defender.card, defender.attacks)) {
        // Collect all cards: defender + attacks
        const allCards = [defender.card, ...defender.attacks];

        // Add to bottom of attacker's stack
        attacker.stack.push(...allCards);

        // Clear the defender position
        target[defenderKey] = null;

        // Check if this exposed the general (both defenders now empty)
        const targetHasNoDefenders = !target.defender1 && !target.defender2;

        if (targetHasNoDefenders) {
          // Track exposed general for priority turn (if it survives)
          const alreadyExposed = G.exposedGenerals.some(e => e.playerId === targetPlayerId);
          if (!alreadyExposed) {
            G.exposedGenerals.push({ playerId: targetPlayerId, exposedBy: ctx.currentPlayer });
          }

          // If attacker's hand is now empty, grant bonus draw for general attack
          if (attacker.hand.length === 0) {
            G.turnState.bonusDrawAllowed = true;
            G.turnState.bonusTarget = targetPlayerId;
          }
        }
      }
    },

    /**
     * Start a defender attack with reveal animation.
     * Initiates the reveal sequence - card moves to center first.
     * @param cardId - Card from hand to attack with
     * @param targetPlayerId - The player being attacked
     * @param slot - 1 or 2, indicating which defender to attack
     */
    startDefenderAttack: ({ G, ctx, playerID }, cardId, targetPlayerId, slot = 1) => {
      if (ctx.currentPlayer !== playerID) return INVALID_MOVE;
      if (G.setupPhase !== 'play') return;
      const attacker = G.players[ctx.currentPlayer];
      const target = G.players[targetPlayerId];

      if (!target || ctx.currentPlayer === targetPlayerId) return;

      const cardIndex = attacker.hand.indexOf(cardId);
      if (cardIndex === -1) return; // Card not in hand

      const defenderKey = slot === 2 ? 'defender2' : 'defender1';
      const defender = target[defenderKey];
      if (!defender || !defender.card) return; // No defender in that slot

      // Pre-calculate if this attack will defeat the defender
      const willDefeat = attackExceedsTarget(defender.card, [...defender.attacks, cardId]);

      // Remove card from hand (it's now "in transit" to reveal area)
      attacker.hand.splice(cardIndex, 1);

      // Set reveal state - animation begins
      G.revealState = {
        attackerId: ctx.currentPlayer,
        attackCard: cardId,
        targetPlayerId: targetPlayerId,
        targetSlot: slot,
        phase: 'revealing',
        willDefeat: willDefeat,
      };
      G.setupPhase = 'revealing';
    },

    /**
     * Initiate a general attack with reveal animation.
     * Similar to startDefenderAttack but targets an exposed general.
     * @param cardId - Card from hand to attack with
     * @param targetPlayerId - The player being attacked
     */
    startGeneralAttack: ({ G, ctx, playerID }, cardId, targetPlayerId) => {
      if (ctx.currentPlayer !== playerID) return INVALID_MOVE;
      if (G.setupPhase !== 'play') return;
      const attacker = G.players[ctx.currentPlayer];
      const target = G.players[targetPlayerId];

      if (!target || ctx.currentPlayer === targetPlayerId) return;
      if (!target.general || !target.general.card) return; // Target already eliminated

      // In bonus mode, can only attack the exposed general
      if (G.turnState.bonusTarget && G.turnState.bonusTarget !== targetPlayerId) {
        return;
      }

      // Can only attack general if no defenders
      if (target.defender1 || target.defender2) return;

      const cardIndex = attacker.hand.indexOf(cardId);
      if (cardIndex === -1) return; // Card not in hand

      // Pre-calculate if this attack will defeat the general
      const willDefeat = attackExceedsTarget(target.general.card, [...target.general.attacks, cardId]);

      // Remove card from hand (it's now "in transit" to reveal area)
      attacker.hand.splice(cardIndex, 1);

      // Set reveal state - animation begins
      G.revealState = {
        attackerId: ctx.currentPlayer,
        attackCard: cardId,
        targetPlayerId: targetPlayerId,
        targetSlot: 'general', // Marker for general attack
        phase: 'revealing',
        willDefeat: willDefeat,
      };
      G.setupPhase = 'revealing';
    },

    /**
     * Advance the reveal animation to the next phase.
     * Called by the UI after each animation step completes.
     */
    advanceRevealPhase: ({ G, ctx }) => {
      if (G.setupPhase !== 'revealing' || !G.revealState) return;

      const { attackCard, targetPlayerId, targetSlot, phase, willDefeat, attackerId } = G.revealState;
      const attacker = G.players[attackerId];
      const target = G.players[targetPlayerId];
      const defenderKey = targetSlot === 2 ? 'defender2' : 'defender1';
      const defender = target[defenderKey];

      switch (phase) {
        case 'revealing':
          // Card revealed at center, now move to target and fan
          if (targetSlot === 'general') {
            target.general.attacks.push(attackCard);
          } else if (defender) {
            defender.attacks.push(attackCard);
          }
          G.revealState.phase = 'fanning';
          break;

        case 'fanning':
          // Card is fanned on defender
          if (willDefeat) {
            // Trigger collapse animation
            G.revealState.phase = 'collapsing';
          } else {
            // Attack doesn't defeat - done, return to play
            G.revealState = null;
            G.setupPhase = 'play';
          }
          break;

        case 'collapsing':
          // Cards collapsed, now pause to show the stack
          G.revealState.phase = 'pausing';
          break;

        case 'pausing':
          // Pause complete - execute the defeat logic
          if (targetSlot === 'general') {
            // General defeated - trigger redistribution
            const defeatedId = targetPlayerId;

            // Collect and shuffle cards from defeated player
            const collectedCards = collectDefeatedPlayerCards(target);
            shuffleArray(collectedCards);
            clearDefeatedPlayerCards(target);

            // Build remaining players list in turn order (starting from attacker)
            const numPlayers = Object.keys(G.players).length;
            const remainingPlayers = [];
            for (let i = 0; i < numPlayers; i++) {
              const playerId = String((parseInt(attackerId) + i) % numPlayers);
              if (playerId !== defeatedId && G.players[playerId].general?.card) {
                remainingPlayers.push(playerId);
              }
            }

            // Clear reveal state before changing phase
            G.revealState = null;

            // Only redistribute if there are remaining players and cards to distribute
            if (remainingPlayers.length > 0 && collectedCards.length > 0) {
              G.redistributionState = {
                defeatedPlayerId: defeatedId,
                collectedCards,
                currentRecipientIdx: 0,
                remainingPlayers,
              };
              G.setupPhase = 'redistributing';
            } else {
              // No cards to redistribute - check for winner immediately
              const winnerId = detectWinner(G);
              if (winnerId) {
                G.setupPhase = 'gameEnding';
                G.gameEndingState = { winnerId, step: 0 };
              } else {
                G.setupPhase = 'play';
              }
            }
          } else if (defender) {
            // Defender defeated - collect cards to attacker's stack
            const allCards = [defender.card, ...defender.attacks];

            // Add to bottom of attacker's stack
            attacker.stack.push(...allCards);

            // Clear the defender position
            target[defenderKey] = null;

            // Check if this exposed the general (both defenders now empty)
            const targetHasNoDefenders = !target.defender1 && !target.defender2;

            if (targetHasNoDefenders) {
              // Track exposed general for priority turn (if it survives)
              const alreadyExposed = G.exposedGenerals.some(e => e.playerId === targetPlayerId);
              if (!alreadyExposed) {
                G.exposedGenerals.push({ playerId: targetPlayerId, exposedBy: attackerId });
              }

              // If attacker's hand is now empty, grant bonus draw for general attack
              if (attacker.hand.length === 0) {
                G.turnState.bonusDrawAllowed = true;
                G.turnState.bonusTarget = targetPlayerId;
              }
            }

            // Clear reveal state and return to play
            G.revealState = null;
            G.setupPhase = 'play';
          }
          break;
      }
    },

    /**
     * Attack an opponent's general with a card from hand.
     * Only possible when target has no defenders.
     * If cumulative attack value exceeds general, target is eliminated.
     * In bonus draw mode, can only attack the bonusTarget.
     * @param cardId - Card from hand to attack with
     * @param targetPlayerId - The player being attacked
     */
    attackGeneral: ({ G, ctx, playerID }, cardId, targetPlayerId) => {
      if (ctx.currentPlayer !== playerID) return INVALID_MOVE;
      if (G.setupPhase !== 'play') return;
      const attacker = G.players[ctx.currentPlayer];
      const target = G.players[targetPlayerId];

      if (!target || ctx.currentPlayer === targetPlayerId) return;
      if (!target.general || !target.general.card) return; // Target already eliminated

      // In bonus mode, can only attack the exposed general
      if (G.turnState.bonusTarget && G.turnState.bonusTarget !== targetPlayerId) {
        return;
      }

      // Can only attack general if no defenders
      if (target.defender1 || target.defender2) return;

      const cardIndex = attacker.hand.indexOf(cardId);
      if (cardIndex === -1) return; // Card not in hand

      // Remove card from hand and add to general's attacks
      attacker.hand.splice(cardIndex, 1);
      target.general.attacks.push(cardId);

      // Check if attack exceeds general value
      if (attackExceedsTarget(target.general.card, target.general.attacks)) {
        // Target player is eliminated - trigger redistribution
        const defeatedId = targetPlayerId;

        // Collect and shuffle cards from defeated player
        const collectedCards = collectDefeatedPlayerCards(target);
        shuffleArray(collectedCards);
        clearDefeatedPlayerCards(target);

        // Build remaining players list in turn order (starting from attacker)
        const remainingPlayers = [];
        for (let i = 0; i < ctx.numPlayers; i++) {
          const playerId = String((parseInt(ctx.currentPlayer) + i) % ctx.numPlayers);
          if (playerId !== defeatedId && G.players[playerId].general?.card) {
            remainingPlayers.push(playerId);
          }
        }

        // Only redistribute if there are remaining players and cards to distribute
        if (remainingPlayers.length > 0 && collectedCards.length > 0) {
          G.redistributionState = {
            defeatedPlayerId: defeatedId,
            collectedCards,
            currentRecipientIdx: 0,
            remainingPlayers,
          };
          G.setupPhase = 'redistributing';
        } else {
          // No cards to redistribute - check for winner immediately
          const winnerId = detectWinner(G);
          if (winnerId) {
            G.setupPhase = 'gameEnding';
            G.gameEndingState = { winnerId, step: 0 };
          }
        }
      }
    },

    /**
     * Use an Ace to interact with a defender.
     * - Own defender: Ace replaces defender, old cards go to own stack
     * - Opponent's defender: Ace defeats defender instantly, all cards go to attacker
     * @param aceCardId - Ace card from hand
     * @param targetPlayerId - The player whose defender to target
     * @param slot - 1 or 2, indicating which defender to target
     */
    replaceDefenderWithAce: ({ G, ctx, playerID }, aceCardId, targetPlayerId, slot = 1) => {
      if (ctx.currentPlayer !== playerID) return INVALID_MOVE;
      if (G.setupPhase !== 'play') return;
      const attacker = G.players[ctx.currentPlayer];
      const target = G.players[targetPlayerId];
      const isOwnDefender = ctx.currentPlayer === targetPlayerId;

      if (!target) return;

      const cardIndex = attacker.hand.indexOf(aceCardId);
      if (cardIndex === -1) return; // Card not in hand
      if (!isAce(aceCardId)) return; // Must be an ace

      const defenderKey = slot === 2 ? 'defender2' : 'defender1';
      const defender = target[defenderKey];
      if (!defender || !defender.card) return; // No defender in that slot

      // Remove ace from hand
      attacker.hand.splice(cardIndex, 1);

      // Collect all cards from defender: defender + attacks
      const allCards = [defender.card, ...defender.attacks];

      // Loot ALWAYS goes to attacker (current player)
      attacker.stack.push(...allCards);

      if (isOwnDefender) {
        // Own defender: Ace becomes new defender
        target[defenderKey] = { card: aceCardId, attacks: [] };
      } else {
        // Opponent's defender: Ace also goes to attacker stack, position cleared
        attacker.stack.push(aceCardId);
        target[defenderKey] = null;

        // Check if this exposed the general (both defenders now empty)
        const targetHasNoDefenders = !target.defender1 && !target.defender2;

        if (targetHasNoDefenders) {
          // Track exposed general for priority turn (if it survives)
          const alreadyExposed = G.exposedGenerals.some(e => e.playerId === targetPlayerId);
          if (!alreadyExposed) {
            G.exposedGenerals.push({ playerId: targetPlayerId, exposedBy: ctx.currentPlayer });
          }

          // If attacker's hand is now empty, grant bonus draw for general attack
          if (attacker.hand.length === 0) {
            G.turnState.bonusDrawAllowed = true;
            G.turnState.bonusTarget = targetPlayerId;
          }
        }
      }
    },

    /**
     * Stash a card from hand (only 1 card can be stashed).
     */
    stashCard: ({ G, ctx, playerID }, cardId) => {
      if (ctx.currentPlayer !== playerID) return INVALID_MOVE;
      if (G.setupPhase !== 'play') return;
      const player = G.players[ctx.currentPlayer];
      const cardIndex = player.hand.indexOf(cardId);

      if (cardIndex === -1) return; // Card not in hand
      if (player.stash !== null) return; // Already have a stashed card

      player.hand.splice(cardIndex, 1);
      player.stash = cardId;
    },

    /**
     * Reveal stashed card and add to hand.
     * Can only be done at start of turn (before drawing).
     */
    revealStash: ({ G, ctx, playerID }) => {
      if (ctx.currentPlayer !== playerID) return INVALID_MOVE;
      if (G.setupPhase !== 'play') return;
      const player = G.players[ctx.currentPlayer];

      if (player.stash === null) return; // No stashed card
      if (G.turnState.hasDrawn) return; // Can only unstash at start of turn
      if (G.turnState.hasUnstashed) return; // Already unstashed

      player.hand.push(player.stash);
      player.stash = null;
      G.turnState.hasUnstashed = true;
    },

    /**
     * Draw a bonus card after exposing an opponent's general.
     * Only allowed when bonusDrawAllowed is true.
     */
    drawBonusCard: ({ G, ctx, playerID }) => {
      if (ctx.currentPlayer !== playerID) return INVALID_MOVE;
      if (G.setupPhase !== 'play') return;
      if (!G.turnState.bonusDrawAllowed) return;

      const player = G.players[ctx.currentPlayer];
      if (player.stack.length < 1) return;

      const card = player.stack.shift();
      player.hand.push(card);

      G.turnState.bonusDrawAllowed = false;
    },

    /**
     * Use an Ace to replace own general (self-saving move).
     * Old general + attack cards go to player's stack.
     * Ace becomes new general with empty attacks.
     * @param aceCardId - Ace card from hand
     */
    replaceOwnGeneralWithAce: ({ G, ctx, playerID }, aceCardId) => {
      if (ctx.currentPlayer !== playerID) return INVALID_MOVE;
      if (G.setupPhase !== 'play') return;
      const player = G.players[ctx.currentPlayer];

      // Validate ace in hand
      const cardIndex = player.hand.indexOf(aceCardId);
      if (cardIndex === -1) return;
      if (!isAce(aceCardId)) return;

      // Must have a general to replace
      if (!player.general || !player.general.card) return;

      // Remove ace from hand
      player.hand.splice(cardIndex, 1);

      // Collect old general + attacks to player's stack
      const loot = [player.general.card, ...player.general.attacks];
      player.stack.push(...loot);

      // Ace becomes new general
      player.general = { card: aceCardId, attacks: [] };
    },

    /**
     * End the current player's turn.
     * Can only be done when hand is empty.
     * Handles priority queue for exposed generals.
     */
    endTurn: ({ G, ctx, events, playerID }) => {
      if (ctx.currentPlayer !== playerID) return INVALID_MOVE;
      if (G.setupPhase !== 'play') return;
      const player = G.players[ctx.currentPlayer];

      if (player.hand.length > 0) return; // Can't end with cards in hand

      // Process exposed generals that survived this turn
      for (const exposed of G.exposedGenerals) {
        const target = G.players[exposed.playerId];
        // Only queue if general survived (still has card)
        if (target.general && target.general.card !== null) {
          // Don't add if already in queue
          if (!G.priorityQueue.includes(exposed.playerId)) {
            G.priorityQueue.push(exposed.playerId);
          }
        }
      }
      G.exposedGenerals = []; // Clear for next turn

      // Determine next player
      if (G.priorityQueue.length > 0) {
        const nextPlayer = G.priorityQueue.shift();
        G.currentPriorityPlayer = nextPlayer;
        events.endTurn({ next: nextPlayer });
      } else {
        G.currentPriorityPlayer = null;
        events.endTurn(); // Normal order
      }
    },

    /**
     * Execute one step of card redistribution after player defeat.
     * Moves one card from collected pile to next recipient's stack bottom.
     * Called repeatedly with delays for animated effect.
     */
    redistributeStep: ({ G, ctx, events }) => {
      if (G.setupPhase !== 'redistributing' || !G.redistributionState) return;

      const rs = G.redistributionState;

      // Check if all cards distributed
      if (rs.collectedCards.length === 0) {
        // Save values before clearing state
        const defeatedPlayerId = rs.defeatedPlayerId;
        const nextPlayer = rs.remainingPlayers[0];
        G.redistributionState = null;

        // Check for winner after redistribution
        const winnerId = detectWinner(G);
        if (winnerId) {
          // Trigger game ending animation
          G.setupPhase = 'gameEnding';
          G.gameEndingState = { winnerId, step: 0 };
        } else {
          // Redistribution complete - return to play
          G.setupPhase = 'play';

          // If current player is the defeated player (starvation case), advance turn
          if (ctx.currentPlayer === defeatedPlayerId) {
            events.endTurn({ next: nextPlayer });
          }
        }
        return;
      }

      // Get next recipient (round-robin)
      const recipientId = rs.remainingPlayers[rs.currentRecipientIdx];
      const recipient = G.players[recipientId];

      // Move one card to bottom of recipient's stack (unshift adds to index 0)
      const card = rs.collectedCards.shift();
      recipient.stack.unshift(card);

      // Advance to next recipient
      rs.currentRecipientIdx = (rs.currentRecipientIdx + 1) % rs.remainingPlayers.length;
    },

    /**
     * Execute one step of game ending animation.
     * Moves one hand card to its player's stack.
     * Called repeatedly with delays for animated effect.
     */
    gameEndingStep: ({ G }) => {
      if (G.setupPhase !== 'gameEnding' || !G.gameEndingState) return;

      // Find a player with hand cards and move one to their stack
      for (const [playerId, player] of Object.entries(G.players)) {
        if (player.hand.length > 0) {
          const card = player.hand.pop();
          player.stack.push(card);
          G.gameEndingState.step++;
          return;
        }
      }

      // All hand cards moved - transition to finished
      G.setupPhase = 'finished';
    },
  },

  /**
   * Turn configuration.
   * activePlayers: { all: Stage.NULL } allows any player to make moves,
   * which is needed for the setup/dealing phase where Player 0 calls
   * dealStep repeatedly regardless of turn order.
   */
  turn: {
    activePlayers: { all: Stage.NULL },
    order: {
      first: () => 0,
      next: ({ G, ctx }) => {
        // Find next alive player (skipping defeated players)
        let next = (ctx.playOrderPos + 1) % ctx.numPlayers;
        for (let i = 0; i < ctx.numPlayers; i++) {
          const playerId = String(next);
          if (G.players[playerId].general?.card) {
            return next;
          }
          next = (next + 1) % ctx.numPlayers;
        }
        return undefined; // No alive players (shouldn't happen - game should have ended)
      },
    },
    onBegin: ({ G, ctx }) => {
      // Skip processing during setup and redistribution phases
      if (G.setupPhase !== 'play') {
        G.turnState = {
          hasDrawn: false,
          hasUnstashed: false,
          hasMadeFirstMove: false,
          bonusDrawAllowed: false,
          bonusTarget: null,
          isPriorityTurn: false,
        };
        return;
      }

      const player = G.players[ctx.currentPlayer];

      // Check if this is a priority turn
      const isPriorityTurn = G.currentPriorityPlayer === ctx.currentPlayer;

      // Determine draw requirements
      const requiredCards = isPriorityTurn ? 3 : 2;

      // Check for starvation
      const hasStash = player.stash !== null;
      const stackCards = player.stack.length;

      // Can meet requirements if:
      // - Has enough cards in stack, OR
      // - Has stash and (stack + 1) >= required (unstash + draw less)
      const canMeetWithStack = stackCards >= requiredCards;
      const canMeetWithStash = hasStash && (stackCards >= requiredCards - 1);
      const canMeetRequirements = canMeetWithStack || canMeetWithStash;

      if (!canMeetRequirements) {
        // Starvation - trigger redistribution (for 3+ player games) or elimination (2 player)
        const defeatedId = ctx.currentPlayer;

        // Collect and shuffle cards from defeated player
        const collectedCards = collectDefeatedPlayerCards(player);
        shuffleArray(collectedCards);
        clearDefeatedPlayerCards(player);

        // Build remaining players list in turn order (starting from next player)
        const remainingPlayers = [];
        for (let i = 1; i < ctx.numPlayers; i++) {
          const playerId = String((parseInt(defeatedId) + i) % ctx.numPlayers);
          if (G.players[playerId].general?.card) {
            remainingPlayers.push(playerId);
          }
        }

        // Only redistribute if there are remaining players and cards to distribute
        if (remainingPlayers.length > 0 && collectedCards.length > 0) {
          G.redistributionState = {
            defeatedPlayerId: defeatedId,
            collectedCards,
            currentRecipientIdx: 0,
            remainingPlayers,
          };
          G.setupPhase = 'redistributing';
        } else {
          // No cards to redistribute - check for winner immediately
          const winnerId = detectWinner(G);
          if (winnerId) {
            G.setupPhase = 'gameEnding';
            G.gameEndingState = { winnerId, step: 0 };
          }
        }
      }

      // Clear priority player marker (it's been consumed)
      G.currentPriorityPlayer = null;

      // Reset turn state
      G.turnState = {
        hasDrawn: false,
        hasUnstashed: false,
        hasMadeFirstMove: false,
        bonusDrawAllowed: false,
        bonusTarget: null,
        isPriorityTurn: isPriorityTurn,
      };
    },
  },

  /**
   * End condition - check for winner.
   * Returns { winner: playerId } only when game ending animation is complete.
   */
  endIf: ({ G }) => {
    // Only return winner after gameEnding animation completes (phase = 'finished')
    if (G.setupPhase === 'finished') {
      const winnerId = G.gameEndingState?.winnerId;
      if (winnerId !== undefined) {
        return { winner: winnerId };
      }
      // Edge case: no winner (shouldn't happen, but handle gracefully)
      return { draw: true };
    }

    return undefined; // Game continues
  },
};

export default WaterlooGame;
